import { writable } from 'svelte/store';

export type VersionPolicyStatus = 'ok' | 'warn' | 'required' | 'policy-unavailable';

export type VersionPolicyState = {
	status: VersionPolicyStatus;
	latest?: string;
	showWarn: boolean;
	updatedAt: number;
};

const VERSION_POLICY_HEADER = 'X-DS-Version-Policy';

const statusPriority: Record<VersionPolicyStatus, number> = {
	ok: 0,
	warn: 1,
	'policy-unavailable': 2,
	required: 3,
};

const dismissedWarnKeys = new Set<string>();

let currentState: VersionPolicyState = {
	status: 'ok',
	showWarn: false,
	updatedAt: Date.now(),
};

const state = writable<VersionPolicyState>(currentState);

function reset() {
	dismissedWarnKeys.clear();
	currentState = {
		status: 'ok',
		showWarn: false,
		updatedAt: Date.now(),
	};
	state.set(currentState);
}

export function parseVersionPolicyHeaderValue(headerValue: string): {
	status?: VersionPolicyStatus;
	latest?: string;
} {
	const entries = headerValue
		.split(';')
		.map((part) => part.trim())
		.filter(Boolean)
		.map((part) => {
			const eq = part.indexOf('=');
			if (eq < 0) return null;
			return [part.slice(0, eq).trim(), part.slice(eq + 1).trim()] as const;
		})
		.filter((entry): entry is readonly [string, string] => entry !== null);

	const map = new Map(entries);
	const status = map.get('status');
	if (
		status !== 'ok' &&
		status !== 'warn' &&
		status !== 'required' &&
		status !== 'policy-unavailable'
	) {
		return {};
	}

	return {
		status,
		latest: map.get('latest'),
	};
}

function warningDismissedKey(userId: string | undefined, latest: string | undefined): string {
	return `ds:${userId ?? 'anon'}:versionWarnDismissed:${latest ?? 'unknown'}`;
}

function safeSessionGetBoolean(key: string): boolean {
	try {
		if (typeof window === 'undefined') return false;
		const raw = window.sessionStorage.getItem(key);
		if (raw === null) return false;
		return JSON.parse(raw) === true;
	} catch {
		return false;
	}
}

function safeSessionSetBoolean(key: string, value: boolean) {
	try {
		if (typeof window === 'undefined') return;
		if (!value) {
			window.sessionStorage.removeItem(key);
			return;
		}
		window.sessionStorage.setItem(key, JSON.stringify(true));
	} catch {
		// Ignore storage failures.
	}
}

function isWarnDismissedForSession(
	latest: string | undefined,
	userId: string | undefined,
): boolean {
	const key = warningDismissedKey(userId, latest);
	if (dismissedWarnKeys.has(key)) {
		return true;
	}

	if (!safeSessionGetBoolean(key)) {
		return false;
	}

	dismissedWarnKeys.add(key);
	return true;
}

function buildState(
	status: VersionPolicyStatus,
	latest: string | undefined,
	userId: string | undefined,
): Omit<VersionPolicyState, 'updatedAt'> {
	return {
		status,
		latest,
		showWarn: status === 'warn' ? !isWarnDismissedForSession(latest, userId) : false,
	};
}

function commitState(next: Omit<VersionPolicyState, 'updatedAt'>): boolean {
	if (
		currentState.status === next.status &&
		currentState.latest === next.latest &&
		currentState.showWarn === next.showWarn
	) {
		return false;
	}

	currentState = {
		...next,
		updatedAt: Date.now(),
	};
	state.set(currentState);
	return true;
}

function setPolicy(
	status: VersionPolicyStatus,
	latest: string | undefined,
	userId: string | undefined,
): boolean {
	const next = buildState(status, latest, userId);
	if (status === 'ok') {
		return commitState(next);
	}

	if (statusPriority[status] < statusPriority[currentState.status]) {
		return false;
	}

	return commitState(next);
}

function applyHeader(headerValue: string, userId: string | undefined): boolean {
	const parsed = parseVersionPolicyHeaderValue(headerValue);
	if (!parsed.status) {
		return false;
	}

	setPolicy(parsed.status, parsed.latest, userId);

	return true;
}

function applyResponse(response: Response, errorCode?: string, userId?: string) {
	const headerValue = response.headers.get(VERSION_POLICY_HEADER);
	if (headerValue && applyHeader(headerValue, userId)) {
		return;
	}

	if (response.status === 426) {
		setPolicy('required', undefined, userId);
		return;
	}

	if (response.status === 503 && errorCode === 'VERSION_POLICY_UNAVAILABLE') {
		setPolicy('policy-unavailable', undefined, userId);
	}
}

function dismissWarnForSession(userId?: string) {
	if (currentState.status !== 'warn') {
		return;
	}

	const key = warningDismissedKey(userId, currentState.latest);
	dismissedWarnKeys.add(key);
	safeSessionSetBoolean(key, true);
	commitState({
		status: currentState.status,
		latest: currentState.latest,
		showWarn: false,
	});
}

export default {
	state,
	applyResponse,
	reset,
	dismissWarnForSession,
};
